/*
 * Displays the list of items in a scanned tote.
 */

import React, { useCallback, useMemo, useState, useEffect } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import Input from "@amzn/meridian/input";
import Checkbox from "@amzn/meridian/checkbox";
import Loader from "@amzn/meridian/loader";
import { Sheet, Divider } from "@amzn/meridian";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import alertKnockoutTokens from "@amzn/meridian-tokens/base/icon/alert-knockout";
import infoKnockoutTokens from "@amzn/meridian-tokens/base/icon/info-knockout";
import { useLocation, useNavigate } from "react-router-dom";
import { ToteItem } from "src/types/toteTypes";
import { ContainerUsingFor, ItemFlowContext } from "src/types/containerTypes";
import { fetchToteItems } from "src/services/toteService";
import { getItemInformation } from "src/services/classificationService";
import { useAppSelector } from "src/store/store";
import BarcodeIcon from "src/components/UI/BarcodeIcon";
import PillButton from "src/components/UI/PillButton";
import ToteItemCard from "./ToteItemCard";
import {
  getWasteProfileIntersection,
  getDonationCategoryIntersection,
} from "src/utils/wasteProfileUtils";
import { isSupportedToteItem } from "src/utils/toteItemUtils";
import "./toteItems.scss";

interface ToteItemsLocationState {
  scannableId: string;
  toteContainerId: number;
  items: ToteItem[];
}

const ToteItemsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as ToteItemsLocationState | undefined;

  const [toteItems, setToteItems] = useState<ToteItem[]>(locationState?.items ?? []);
  const [isFetchingItems, setIsFetchingItems] = useState(true);
  const [filterQuery, setFilterQuery] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [itemError, setItemError] = useState<string | null>(null);
  const [loadingItemId, setLoadingItemId] = useState<string | null>(null);
  const [longPressedItem, setLongPressedItem] = useState<ToteItem | null>(null);

  const scannableId = locationState?.scannableId ?? "UNKNOWN";
  const { activeSiteCode: siteCode } = useAppSelector((state) => state.user);
  const toteContainerId = locationState?.toteContainerId ?? 0;

  // Fetch tote items — skipped if already provided in router state,otherwise will refetch the tote items.
  useEffect(() => {
    if (!locationState) return;
    // Items already provided — skip the fetch (initial load from ToteScanPage)
    if (locationState.items) {
      setIsFetchingItems(false);
      return;
    }
    const fetchItems = async () => {
      setIsFetchingItems(true);
      try {
        const { items: updatedItems } = await fetchToteItems(locationState.scannableId, siteCode);
        setToteItems(updatedItems);
      } catch (err) {
        setItemError("Failed to load tote items. Please go back and scan the tote again.");
      } finally {
        setIsFetchingItems(false);
      }
    };
    fetchItems();
  }, [locationState, siteCode]);

  // Filter items based on the search query
  const filteredItems = useMemo(() => {
    const searchQuery = filterQuery.trim().toLowerCase();
    if (!searchQuery) return toteItems;
    return toteItems.filter(
      (item) =>
        item.name?.toLowerCase().includes(searchQuery) ||
        item.asin?.toLowerCase().includes(searchQuery) ||
        item.fcSku.toLowerCase().includes(searchQuery),
    );
  }, [toteItems, filterQuery]);

  // Handle selecting/unselecting an item (in select mode)
  const handleToggleItemSelection = (id: string) => {
    setSelectedIds((prev) => {
      const updatedSelectedIds = new Set(prev);
      updatedSelectedIds.has(id) ? updatedSelectedIds.delete(id) : updatedSelectedIds.add(id);
      return updatedSelectedIds;
    });
  };

  //handle toggling select mode on/off
  const handleToggleSelectMode = () => {
    setSelectMode((prev) => {
      const isEnteringSelectMode = !prev;
      if (!isEnteringSelectMode) setSelectedIds(new Set());
      return isEnteringSelectMode;
    });
  };

  const handleItemPress = useCallback(
    async (item: ToteItem, isMissingReport = false) => {
      const identifier = item.asin ?? item.fcSku;
      setItemError(null);
      setLoadingItemId(item.id);
      try {
        const classificationData = await getItemInformation(identifier, siteCode);
        navigate("/classification/results", {
          state: {
            data: classificationData,
            itemFlowContext: {
              type: "tote" as const,
              items: [item],
              scannableId,
              toteContainerId,
              totalItemQuantity: toteItems.reduce((sum, i) => sum + i.quantity, 0),
            },
            isMissingReport,
          },
        });
      } catch {
        setItemError(`Could not find item details for ASIN: ${identifier}.`);
      } finally {
        setLoadingItemId(null);
      }
    },
    [siteCode, navigate, scannableId, toteContainerId, toteItems],
  );

  const handleAddToContainer = () => {
    const selectedToteItems = toteItems.filter((item) => selectedIds.has(item.id));
    const totalItemQuantity = toteItems.reduce((sum, item) => sum + item.quantity, 0);
    navigate("/tote/chooseContainer", {
      state: {
        itemFlowContext: {
          type: "tote" as const,
          items: selectedToteItems,
          scannableId,
          toteContainerId,
          totalItemQuantity,
        },
        wasteProfileIds: getWasteProfileIntersection(selectedToteItems),
        donationCategoryIds: [], // donation IDs not needed when discarding to waste container
        usingFor: "WASTE" as ContainerUsingFor,
      },
    });
  };

  const handleDonate = () => {
    const selectedToteItems = toteItems.filter((item) => selectedIds.has(item.id));
    const totalItemQuantity = toteItems.reduce((sum, item) => sum + item.quantity, 0);
    navigate("/tote/chooseContainer", {
      state: {
        itemFlowContext: {
          type: "tote" as const,
          items: selectedToteItems,
          scannableId,
          toteContainerId,
          totalItemQuantity,
        },
        wasteProfileIds: [], // waste profile IDs not needed when donating
        donationCategoryIds: getDonationCategoryIntersection(selectedToteItems),
        usingFor: "DONATION" as ContainerUsingFor,
      },
    });
  };

  // Donation is allowed only if every selected item is donation eligible.
  const areSelectedItemsDonationEligible = useMemo(
    () =>
      toteItems
        .filter((item) => selectedIds.has(item.id))
        .every((item) => item.donationEligibilityInformation?.isEligibleForDonation === true),
    [toteItems, selectedIds],
  );

  if (!locationState) {
    return (
      <Column
        height="100vh"
        alignmentHorizontal="center"
        alignmentVertical="center"
        spacing="400"
        spacingInset="500"
      >
        <Text type="h200">No tote data found</Text>
        <Text color="secondary">Please scan a tote first.</Text>
        <PillButton
          text="Scan a tote"
          color="secondary"
          onClick={() => navigate("/classification/scan")}
        />
      </Column>
    );
  }

  if (isFetchingItems) {
    return (
      <Column
        height="100vh"
        className="app-gradient-bg"
        alignmentHorizontal="center"
        alignmentVertical="center"
        spacing="400"
      >
        <Loader type="circular" size="large" />
        <Text type="h200" color="secondary">
          Refreshing items…
        </Text>
        <Text type="b200" color="secondary">
          TOTE - {scannableId}
        </Text>
      </Column>
    );
  }

  return (
    <Column className="app-gradient-bg pwa-scrollable" spacing="400" spacingInset="none none 600">
      {/* Header — back, tote ID, SELECT toggle */}
      <Row alignmentVertical="center" alignmentHorizontal="justify" spacingInset="500 400 none">
        <Row alignmentVertical="center" spacing="none">
          <Button type="icon" onClick={() => navigate("/classification/scan")}>
            <Icon color="success" tokens={arrowLeftTokens} />
          </Button>
          <Column spacing="none" spacingInset="200">
            <Text type="h100" color="success">
              TOTE - {scannableId}
            </Text>
            <Text type="b100" color="secondary">
              SITE {siteCode}
            </Text>
          </Column>
        </Row>

        <PillButton
          text={selectMode ? "CANCEL" : "SELECT"}
          color="secondary"
          onClick={handleToggleSelectMode}
        />
      </Row>

      {/* Search / filter */}
      <Row spacingInset="200 500" spacing="300" widths={["fill", "fit"]}>
        <Input
          type="text"
          placeholder="Filter by name, ASIN, or FcSku"
          value={filterQuery}
          onChange={(value: string) => setFilterQuery(value)}
        />
        <BarcodeIcon
          size={30}
          className="tote-barcode-icon"
          onClick={() =>
            navigate("/classification/scan", {
              state: {
                title: "Report Extra Item",
                mode: "PRODUCT",
                itemFlowContext: {
                  type: "toteExtraItem",
                  toteContainerId,
                  scannableId,
                } as ItemFlowContext,
              },
            })
          }
        />
      </Row>

      {/* Counter — shows selected item count in select mode, or filtered/total items otherwise */}
      <Row spacingInset="none 500">
        <Text type="b200" color="secondary">
          {selectMode && selectedIds.size > 0
            ? `${selectedIds.size} item${selectedIds.size > 1 ? "s" : ""} selected`
            : `${filteredItems.length}/${toteItems.length} Products left in the tote`}
        </Text>
      </Row>

      {/* Tote items list */}
      <Column spacing="400" spacingInset="none 500 500">
        {itemError && (
          <Text color="error" alignment="center">
            {itemError}
          </Text>
        )}
        {toteItems.length === 0 ? (
          <Column alignmentHorizontal="center" spacingInset="600 none">
            <Text color="secondary" alignment="center">
              This tote is empty. If there are items physically in the tote, use the barcode icon to
              report them as extra items.
            </Text>
          </Column>
        ) : filteredItems.length === 0 ? (
          <Column alignmentHorizontal="center" spacingInset="600 none">
            <Text color="secondary">No items match your filter.</Text>
          </Column>
        ) : (
          filteredItems.map((item) => {
            const isSupported = isSupportedToteItem(item);
            return (
              <Row
                key={item.id}
                alignmentVertical="center"
                spacing="300"
                widths={selectMode ? ["fit", "fill"] : ["fill"]}
              >
                {selectMode && (
                  <Checkbox
                    checked={selectedIds.has(item.id)}
                    onChange={() => handleToggleItemSelection(item.id)}
                    disabled={!isSupported}
                  />
                )}
                <ToteItemCard
                  item={item}
                  onPress={isSupported ? handleItemPress : () => {}}
                  isLoading={loadingItemId === item.id}
                  onLongPress={isSupported ? () => setLongPressedItem(item) : undefined}
                />
              </Row>
            );
          })
        )}
      </Column>

      {/* Action buttons — only visible when at least one item is selected */}
      {selectMode && selectedIds.size > 0 && (
        <Column className="asin-sticky-footer" spacing="300">
          <Row spacing="300">
            <Column>
              <PillButton
                text="DONATE"
                color="outline"
                onClick={handleDonate}
                disabled={!areSelectedItemsDonationEligible}
              />
            </Column>
            <Column>
              <PillButton text="ADD TO CONTAINER" color="success" onClick={handleAddToContainer} />
            </Column>
          </Row>
        </Column>
      )}

      {/* Long press context menu */}
      <Sheet
        type="overlay"
        side="bottom"
        open={longPressedItem !== null}
        onClose={() => setLongPressedItem(null)}
        spacingInset="500"
      >
        <Column spacing="none">
          {longPressedItem && <ToteItemCard item={longPressedItem} onPress={() => {}} />}
          <div
            onClick={() => {
              if (!longPressedItem) return;
              navigate("/tote/confirmQuantity", {
                state: {
                  itemFlowContext: {
                    type: "tote" as const,
                    items: [longPressedItem],
                    scannableId,
                    toteContainerId,
                    totalItemQuantity: toteItems.reduce((sum, i) => sum + i.quantity, 0),
                  },
                  isMissingReport: true,
                },
              });
            }}
          >
            <Row spacing="300" alignmentVertical="center" spacingInset="400 none">
              <Icon tokens={alertKnockoutTokens} color="error" />
              <Text type="b300" color="error">
                Mark product as missing
              </Text>
            </Row>
          </div>
          <Divider />
          <div
            onClick={() => {
              if (!longPressedItem) return;
              handleItemPress(longPressedItem, true);
              setLongPressedItem(null);
            }}
          >
            <Row spacing="300" alignmentVertical="center" spacingInset="400 none">
              <Icon tokens={infoKnockoutTokens} />
              <Text type="b300">View product details</Text>
            </Row>
          </div>
        </Column>
      </Sheet>
    </Column>
  );
};

export default ToteItemsPage;
